/**
 * @module components/common/useStackedTopNDraftFields
 * @description 堆叠/展示限制 TopN（stackedCount / stackedDynamicState / stackedShowOthers）
 *
 * 与 X 轴 TopN（count/dynamicState/showOthers）字段独立；未落库时调用方可覆写 defaultCount
 *（堆叠默认 20、折线展示限制默认 10）。
 */
import { useCallback } from 'react';
import {
  useChartSettingsSelector,
  usePatchDraft,
  useSettingState,
} from '../../../context';
import { DEFAULT_STACKED_COUNT } from '../../../utils/constants';
import type { ITopNFieldGroupValue } from '.';

export function useStackedTopNDraftFields(
  defaultCount = DEFAULT_STACKED_COUNT,
  defaultDynamicState = false,
): ITopNFieldGroupValue {
  const patchDraft = usePatchDraft();
  const { value: dynamicState, setValue: setDynamicState } = useSettingState(
    'stackedDynamicState',
  );
  const { value: showOthers, setValue: setShowOthers } = useSettingState(
    'stackedShowOthers',
  );
  const { value: schemaCount, setValue: setCount } =
    useSettingState('stackedCount');

  const hasStoredCount = useChartSettingsSelector(
    s => 'stackedCount' in s.draft || 'stackedCount' in s.base,
  );
  const hasStoredDynamicState = useChartSettingsSelector(
    s =>
      'stackedDynamicState' in s.draft || 'stackedDynamicState' in s.base,
  );

  const count = hasStoredCount ? schemaCount : defaultCount;
  const resolvedDynamicState = hasStoredDynamicState
    ? dynamicState
    : defaultDynamicState;

  const onCountChange = useCallback(
    (value: number) => {
      if (resolvedDynamicState) {
        patchDraft({
          stackedCount: value,
          stackedDynamicState: true,
          stackedShowOthers: showOthers,
        });
        return;
      }
      setCount(value);
    },
    [patchDraft, resolvedDynamicState, setCount, showOthers],
  );

  const onDynamicStateChange = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        patchDraft({
          stackedDynamicState: true,
          stackedCount: count,
          stackedShowOthers: showOthers,
        });
        return;
      }
      setDynamicState(false);
    },
    [count, patchDraft, setDynamicState, showOthers],
  );

  const onShowOthersChange = useCallback(
    (value: string) => {
      if (resolvedDynamicState) {
        patchDraft({
          stackedShowOthers: value,
          stackedDynamicState: true,
          stackedCount: count,
        });
        return;
      }
      setShowOthers(value);
    },
    [count, patchDraft, resolvedDynamicState, setShowOthers],
  );

  return {
    count,
    dynamicState: resolvedDynamicState,
    showOthers,
    onCountChange,
    onDynamicStateChange,
    onShowOthersChange,
  };
}
